import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, billingAPI } from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);

  const isAuthenticated = !!token && !!user;

  const isSubscriptionActive = subscriptionStatus === 'active' ||
    (subscriptionStatus === 'trialing' &&
     subscriptionData?.trial_ends_at &&
     new Date(subscriptionData.trial_ends_at) > new Date());

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Fetch subscription status from billing service
  const fetchSubscriptionStatus = async () => {
    try {
      const response = await billingAPI.getSubscription();
      const status = response?.status || null;
      setSubscriptionStatus(status);
      setSubscriptionData(response);
      return status;
    } catch (error) {
      // No subscription yet or error = needs checkout
      console.log('No subscription found or error fetching:', error);
      setSubscriptionStatus(null);
      setSubscriptionData(null);
      return null;
    }
  };

  const verifyToken = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8004';
      const response = await fetch(`${apiUrl}/api/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Normalize snake_case to camelCase (API returns tenant_id, user_id)
          const userData = {
            userId: data.data.user_id,
            tenantId: data.data.tenant_id,
            username: data.data.username,
            email: data.data.email
          };
          setUser(userData);
          // Fetch subscription status from billing service
          await fetchSubscriptionStatus();
        } else {
          console.error('Token verification failed:', data.message);
          logout();
        }
      } else {
        console.error('Token verification HTTP error:', response.status, response.statusText);
        if (response.status === 401 || response.status === 403) {
          logout();
        }
      }
    } catch (error) {
      console.error('Token verification network error:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await authAPI.signIn(username, password);

      if (response.success && response.data) {
        const { access_token, user_id, tenant_id, username: user } = response.data;

        localStorage.setItem('admin_token', access_token);
        setToken(access_token);

        const userData = {
          userId: user_id,
          tenantId: tenant_id,
          username: user,
          email: username
        };

        setUser(userData);

        // Fetch subscription status from billing service
        const subStatus = await fetchSubscriptionStatus();

        // New signup — redirect to Stripe checkout
        const pendingPlan = localStorage.getItem('pending_trial_plan');
        if (pendingPlan && (!subStatus || subStatus === 'incomplete')) {
          return { success: true, redirect: '/admin/complete-setup' };
        }

        // Incomplete checkout (started but didn't finish)
        if (subStatus === 'incomplete') {
          return { success: true, redirect: '/admin/complete-setup' };
        }

        return { success: true, redirect: '/admin' };
      } else {
        return { success: false, message: response.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const signup = async (username, email, password, plan = 'starter') => {
    try {
      const response = await authAPI.signUp(username, email, password, plan);

      if (response.success) {
        // Store plan and email for trial initialization after login
        localStorage.setItem('pending_trial_plan', plan);
        localStorage.setItem('pending_trial_email', email);
      }

      return response;
    } catch (error) {
      console.error('Signup error:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Signup failed'
      };
    }
  };

  const confirmSignup = async (username, code) => {
    try {
      const response = await authAPI.confirmSignUp(username, code);
      return response;
    } catch (error) {
      console.error('Confirm signup error:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Confirmation failed'
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('pending_trial_plan');
    localStorage.removeItem('pending_trial_email');
    localStorage.removeItem('pending_invite_token');
  };

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    subscriptionStatus,
    subscriptionData,
    isSubscriptionActive,
    fetchSubscriptionStatus,
    login,
    signup,
    confirmSignup,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
